import { useState } from "preact/hooks";
import {
  formatMoney,
  cents,
  type MenuItem,
  type SizeVariant,
  type ItemOption,
  type OrderLineModifier,
  type ModifierId,
} from "@orderman/shared";

export interface SheetResult {
  variant: SizeVariant | null;
  mods: OrderLineModifier[];
  note: string;
}

interface Props {
  item: MenuItem;
  /** Vorbelegung fürs nachträgliche Bearbeiten einer bestehenden Zeile (statt Neu-Hinzufügen). */
  initial?: { variant: SizeVariant | null; mods: OrderLineModifier[]; note: string };
  confirmLabel?: string;
  onConfirm: (r: SheetResult) => void;
  onClose: () => void;
}

/** Option (Beilage/Extra) → Zeilen-Modifier. ID = Label (stabil für Dedup). */
const toMod = (o: ItemOption): OrderLineModifier => ({
  modifierId: o.label as unknown as ModifierId,
  name: o.label,
  priceDeltaCents: o.priceDeltaCents,
});

/** War diese Option (Beilage/Extra) unter den vorbelegten Zeilen-Modifiern? ID = Label. */
const wasSelected = (mods: OrderLineModifier[], o: ItemOption): boolean =>
  mods.some((m) => m.modifierId === (o.label as unknown as ModifierId));

export function ModifierSheet({ item, initial, confirmLabel = "Hinzufügen", onConfirm, onClose }: Props) {
  const variants = item.variants ?? [];
  const beilagen = item.beilagen ?? [];
  const extras = item.extras ?? [];

  const [variantIdx, setVariantIdx] = useState(() => {
    if (initial?.variant) {
      const i = variants.findIndex((v) => v.label === initial.variant!.label);
      if (i >= 0) return i;
    }
    return variants.length > 0 ? 0 : -1;
  });
  const [beilageIdx, setBeilageIdx] = useState(() =>
    initial ? beilagen.findIndex((b) => wasSelected(initial.mods, b)) : -1,
  ); // -1 = keine
  const [extraSel, setExtraSel] = useState<Set<number>>(() => {
    const s = new Set<number>();
    if (initial) extras.forEach((x, i) => wasSelected(initial.mods, x) && s.add(i));
    return s;
  });
  const [note, setNote] = useState(initial?.note ?? "");

  const variant = variantIdx >= 0 ? variants[variantIdx]! : null;
  const base = variant ? Number(variant.priceCents) : Number(item.priceCents);
  const mods: OrderLineModifier[] = [
    ...(beilageIdx >= 0 && beilagen[beilageIdx] ? [toMod(beilagen[beilageIdx]!)] : []),
    ...[...extraSel].sort((a, b) => a - b).map((i) => toMod(extras[i]!)),
  ];
  const total = cents(base + mods.reduce((s, m) => s + Number(m.priceDeltaCents), 0));
  const valid = (variants.length === 0 || variant !== null) && (beilagen.length === 0 || beilageIdx >= 0);

  const toggleExtra = (i: number) =>
    setExtraSel((prev) => {
      const n = new Set(prev);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });

  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="sheet" onClick={(e) => e.stopPropagation()}>
        <div class="sheet-head">
          <span class="sheet-title">{item.shortLabel || item.name}</span>
          <button class="icon-btn dark" onClick={onClose} aria-label="Schließen">✕</button>
        </div>

        <div class="sheet-body">
          {variants.length > 0 && (
            <div class="mgroup">
              <div class="mgroup-head">Größe<span class="mgroup-rule">Pflicht</span></div>
              <div class="mods">
                {variants.map((v, i) => (
                  <button key={v.label} class={`mod ${variantIdx === i ? "on" : ""}`} onClick={() => setVariantIdx(i)}>
                    <span>{v.label}</span>
                    <span class="mod-delta">{formatMoney(v.priceCents)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {beilagen.length > 0 && (
            <div class="mgroup">
              <div class="mgroup-head">Beilage<span class="mgroup-rule">Pflicht</span></div>
              <div class="mods">
                {beilagen.map((b, i) => (
                  <button key={b.label} class={`mod ${beilageIdx === i ? "on" : ""}`} onClick={() => setBeilageIdx(i)}>
                    <span>{b.label}</span>
                    {Number(b.priceDeltaCents) !== 0 && <span class="mod-delta">+{formatMoney(b.priceDeltaCents)}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {extras.length > 0 && (
            <div class="mgroup">
              <div class="mgroup-head">Extras<span class="mgroup-rule">mehrere möglich</span></div>
              <div class="mods">
                {extras.map((x, i) => (
                  <button key={x.label} class={`mod ${extraSel.has(i) ? "on" : ""}`} onClick={() => toggleExtra(i)}>
                    <span>{x.label}</span>
                    {Number(x.priceDeltaCents) !== 0 && <span class="mod-delta">+{formatMoney(x.priceDeltaCents)}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div class="mgroup">
            <div class="mgroup-head">Notiz<span class="mgroup-rule">optional</span></div>
            <textarea
              class="note-input"
              rows={2}
              value={note}
              placeholder="z. B. ohne Zwiebel, gut durch…"
              onInput={(e) => setNote(e.currentTarget.value)}
            />
          </div>
        </div>

        <div class="sheet-foot">
          <span class="sheet-total">{formatMoney(total)}</span>
          <button class="send" disabled={!valid} onClick={() => onConfirm({ variant, mods, note: note.trim() })}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
